import { TEMPLATE_VARIABLES } from '@/components/template-variables-panel';
import { extractPlaceholderKeys } from '@/lib/email-placeholder-utils';

/**
 * Maps each template category code to the variable section names that are
 * allowed when the template belongs to that category.
 *
 * Section names must match the `category` field on the variables defined in
 * `TEMPLATE_VARIABLES` (e.g. "Customer Information", "Transaction Details").
 */
export const CATEGORY_ALLOWED_SECTIONS: Record<string, string[]> = {
  customers: ['Customer Information', 'Transaction Details', 'Company Information', 'System Information'],
  transactions: ['Customer Information', 'Company Information', 'System Information'],
  collections: [
    'Customer Information',
    'Transaction Details',
    'Collection Information',
    'Company Information',
    'System Information',
  ],
  promises: [
    'Payment Information',
    'Customer Information',
    'Transaction Details',
    'Company Information',
    'System Information',
  ],
  disputes: ['Customer Information', 'Transaction Details', 'Company Information', 'System Information'],
  general: ['Company Information', 'System Information'],
};

/**
 * Returns the list of allowed variable section names for a given category code.
 * If the code is not recognised, returns `undefined` (meaning no restriction).
 */
export function getAllowedSections(categoryCode: string | undefined): string[] | undefined {
  if (!categoryCode) return undefined;
  return CATEGORY_ALLOWED_SECTIONS[categoryCode.toLowerCase()];
}

/**
 * Build a `Set` of variable inner-keys (without `<<>>`) that are allowed for
 * the given category code.  Returns `undefined` when there is no restriction.
 */
export function getAllowedVariableKeys(categoryCode: string | undefined): Set<string> | undefined {
  const sections = getAllowedSections(categoryCode);
  if (!sections) return undefined;

  const sectionSet = new Set(sections);
  const keys = new Set<string>();

  for (const v of TEMPLATE_VARIABLES) {
    if (sectionSet.has(v.category)) {
      keys.add(v.variable.replace(/^<<|>>$/g, ''));
    }
  }
  return keys;
}

/**
 * Given a category code and template HTML (subject or body), returns
 * the list of variable keys (with `<<>>` wrapping) that are used in the
 * template but are **not** allowed for the category.
 *
 * Returns an empty array when no category is selected or when all used
 * variables are allowed.
 */
export function findDisallowedVariables(categoryCode: string | undefined, ...htmlParts: string[]): string[] {
  const allowedKeys = getAllowedVariableKeys(categoryCode);
  if (!allowedKeys) return [];

  const usedKeys = new Set<string>();
  for (const html of htmlParts) {
    for (const key of extractPlaceholderKeys(html)) {
      usedKeys.add(key);
    }
  }

  const disallowed: string[] = [];
  for (const key of usedKeys) {
    if (!allowedKeys.has(key)) {
      disallowed.push(`<<${key}>>`);
    }
  }
  return disallowed;
}
